import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import ScrollToTop from "./helpers/scroll-top";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import "./translations/i18n";
import urls from "./urls";
import ForgotPassword from "./pages/other/ForgotPassword";
import { Loader } from "./components/loader/Loader";
import { updateCart } from "./redux/slices/cartSlice";
import CookieBanner from "./components/banner/CookieBanner";
import FirstVisitSignupModal from "./components/promo/FirstVisitSignupModal";
import ReactPixel from "react-facebook-pixel";
import { notification } from "antd";
import RecipesDetail from "./pages/vce/Recipes/RecipesDetail";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import "dayjs/locale/en";
import dayOfYear from "dayjs/plugin/dayOfYear";
import customParseFormat from "dayjs/plugin/customParseFormat";
import LoyaltyProgramModule from "./components/loyalty-program/LoyaltyProgramModule";
import BlogDetail from "./pages/vce/Blog/BlogDetail";
import { ErrorBoundary } from "react-error-boundary";
import { sendExceptionToSlack } from "./services/api/exception";
import ErrorBoundaryFallback from "./components/core/ErrorBoundaryFallback";
import { getPostHogClient } from "./helpers/posthogManager";
import { useProducts } from "./components/hooks/firestore/useProducts";
import { useOrderCapacity } from "./components/hooks/firestore/useOrderCapacity";
import { useNotifications } from "./components/hooks/firestore/useNotifications";
import { DeliveryAddressProvider } from "./contexts/DeliveryAddressProvider";

dayjs.locale("nl");

dayjs.extend(dayOfYear);
dayjs.extend(customParseFormat);

const Home = lazy(() => import("./pages/home/Home"));
// shop pages
const ShopListStandard = lazy(() => import("./pages/shop/ShopList"));
const CategoryLandingPage = lazy(() => import("./pages/shop/CategoryLanding"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));

// account pages
const MyAccount = lazy(() => import("./pages/account/MyAccount"));
const MyOrders = lazy(() => import("./pages/account/MyOrders"));
const MyFavorites = lazy(() => import("./pages/account/MyFavorites"));
const MyAddresses = lazy(() => import("./pages/account/MyAddresses"));

// other pages
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));
const Login = lazy(() => import("./pages/other/Login"));
const Register = lazy(() => import("./pages/other/Register"));
const VerifyEmail = lazy(() => import("./pages/other/VerifyEmail"));
const Verpakking = lazy(() => import("./pages/vce/Verpakking"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Checkout = lazy(() => import("./pages/other/Checkout"));
const OrderConfirmed = lazy(() => import("./pages/other/OrderConfirmed"));
const OrderFailed = lazy(() => import("./pages/other/OrderFailed"));
const OrderProcessing = lazy(() => import("./pages/other/OrderProcessing"));
const PrivacyPolicy = lazy(() => import("./pages/other/PrivacyPolicy"));
const ShippingReturns = lazy(() => import("./pages/other/ShippingReturns"));
const FAQ = lazy(() => import("./pages/other/FAQ"));
const UnsubscribeMails = lazy(() => import("./pages/other/UnsubscribeMails"));

const NotFound = lazy(() => import("./pages/other/NotFound"));

// SEO pages
const Slager = lazy(() => import("./pages/vce/Slager"));
const SlagerHoorn = lazy(() => import("./pages/vce/SlagerHoorn"));
const SlagerMedemblik = lazy(() => import("./pages/vce/SlagerMedemblik"));
const SlagerEnkhuizen = lazy(() => import("./pages/vce/SlagerEnkhuizen"));
const IQGrills = lazy(() => import("./pages/vce/IQGrills"));
const VleesGroothandel = lazy(() => import("./pages/vce/VleesGroothandel"));
const Horeca = lazy(() => import("./pages/vce/Horeca"));
const Winkel = lazy(() => import("./pages/vce/Winkel"));
const Vacatures = lazy(() => import("./pages/vce/Vacatures"));
const ReceptenOverview = lazy(
  () => import("./pages/vce/Recipes/RecipesOverview"),
);
const BlogOverview = lazy(() => import("./pages/vce/Blog/BlogOverview"));
const Grasgevoerd = lazy(() => import("./pages/vce/Grasgevoerd"));
const KerstVlees = lazy(() => import("./pages/vce/KerstVlees"));
const Cadeaubon = lazy(() => import("./pages/vce/Cadeaubon"));
const HalalCertificaten = lazy(() => import("./pages/vce/HalalCertificates"));
const Context = React.createContext({ name: "Default" });

const PageViewTracker = () => {
  const location = useLocation();
  const posthogClient = getPostHogClient();

  useEffect(() => {
    if (posthogClient) {
      posthogClient.capture("$pageview");
    }
  }, [location, posthogClient]);

  return null;
};

const App = () => {
  const dispatch = useDispatch();
  const { data: products } = useProducts();
  const { data: orderCapacity } = useOrderCapacity();
  const { data: notifications } = useNotifications();
  const [api, contextHolder] = notification.useNotification();
  const [capacityNotificationsShown, setCapacityNotificationsShown] =
    useState(false);
  const [shownNotificationIds, setShownNotificationIds] = useState(new Set());
  const contextValue = useMemo(() => ({ name: "notification" }), []);
  const [lastErrorLog, setLastErrorLog] = useState(null);

  useEffect(() => {
    if (products?.length > 0) {
      dispatch(updateCart(products));
    }
  }, [products]);

  useEffect(() => {
    initFbPixel();
    // if (dayjs().week() === 51 && dayjs().day() < 6) showWeekNotification();
    // showChristmasNotification();
    // showPromotionNotification();
  }, []);

  useEffect(() => {
    if (
      !capacityNotificationsShown &&
      !!orderCapacity &&
      orderCapacity.length > 0
    ) {
      showCapacityNotifications();
      setCapacityNotificationsShown(true);
    }
  }, [orderCapacity]);

  useEffect(() => {
    if (notifications && notifications.length > 0) {
      showActiveNotifications();
    }
  }, [notifications]);

  const showCapacityNotifications = () => {
    if (!!orderCapacity && orderCapacity.length > 0) {
      const today = dayjs();

      orderCapacity.forEach((capacityItem) => {
        const dayjsDate = dayjs(capacityItem.date, "DD-MM-YYYY");
        const isFutureDate =
          (dayjsDate.dayOfYear() > today.dayOfYear() &&
            dayjsDate.year() === today.year()) ||
          dayjsDate.year() > today.year();

        if (isFutureDate && capacityItem.limits) {
          capacityItem.limits.forEach((limit) => {
            if (
              limit.currentAmount >= limit.limit &&
              limit.notificationTitle &&
              limit.notificationMessage
            ) {
              api.info({
                message: limit.notificationTitle,
                description: limit.notificationMessage,
                duration: 0,
                placement: "bottom",
              });
            }
          });
        }
      });
    }
  };

  const showActiveNotifications = () => {
    const now = dayjs().unix();

    notifications.forEach((notif) => {
      // Check if notification is within active time window
      if (
        notif.startDateTime <= now &&
        notif.endDateTime >= now &&
        !shownNotificationIds.has(notif.id)
      ) {
        api.info({
          message: notif.title,
          description: notif.message,
          duration: 0,
          placement: "bottom",
          onClose: () => {
            // Mark as shown when user closes it
            setShownNotificationIds((prev) => new Set(prev).add(notif.id));
          },
        });
        // Mark as shown immediately after displaying
        setShownNotificationIds((prev) => new Set(prev).add(notif.id));
      }
    });
  };

  const showPromotionNotification = () => {
    if (!window?.location?.href.includes("bevestigd")) {
      notification.info({
        message: "BBQ-week winactie",
        description:
          "Plaats deze week je bestelling en maak kans op een Ventique airco ter waarde van € 159!",
        duration: 20,
      });
    }
  };

  const showWeekNotification = () => {
    notification.info({
      message: "Wij nemen geen bestellingen meer aan voor deze week",
      description:
        "Wegens drukte nemen wij geen bestellingen meer aan voor deze week. U bent zaterdag van harte welkom bij onze winkelverkoop. U kunt wel alvast een bestelling plaatsen voor volgende week.",
      duration: 20,
      placement: "bottom",
    });
  };

  const showChristmasNotification = () => {
    notification.info({
      message: "Bestel tijdig voor kerst",
      description:
        "Wij hebben een maximaal aantal bestellingen dat wij voor kerst kunnen verwerken. Bestel tijdig om zeker te zijn van het lekkerste vlees tijdens de feestperiode.",
      duration: 8,
      placement: "bottom",
    });
  };

  const initFbPixel = () => {
    const options = {
      autoConfig: true,
      debug: false,
    };
    ReactPixel.init(process.env.REACT_APP_FACEBOOK_PIXEL_ID, null, options);
    ReactPixel.pageView(); // For tracking page view
  };

  const logError = (error) => {
    const now = Date.now();
    if (!lastErrorLog || now - lastErrorLog > 5000) {
      // 5 seconds cooldown
      const currentPage = window.location.pathname;
      sendExceptionToSlack(
        "Client crash op pagina " + currentPage + ": " + error.message,
      );
      setLastErrorLog(now);
    }
  };

  if (!products) return <Loader />;
  return (
    <>
      <ErrorBoundary fallback={<ErrorBoundaryFallback />} onError={logError}>
        <Context.Provider value={contextValue}>
          {contextHolder}
        </Context.Provider>
        <Router>
          <DeliveryAddressProvider>
            <ScrollToTop>
              <FirstVisitSignupModal />
              <PageViewTracker />
              <Suspense fallback={<Loader />}>
                <Routes>
                  {/* Homepage */}
                  <Route path={urls.home} element={<Home />} />
                  {/* Shop pages */}
                  <Route path={urls.shop} element={<ShopListStandard />} />
                  <Route
                    path={urls.shopLandingPage}
                    element={<ShopListStandard />}
                  />
                  <Route
                    path={urls.productsByCategory}
                    element={<ShopListStandard />}
                  />
                  {/* Shop product pages */}
                  <Route path={urls.productDetail} element={<Product />} />
                  {/* Category landing page */}
                  <Route
                    path={urls.categoryLandingPage}
                    element={<CategoryLandingPage />}
                  />
                  <Route
                    path={urls.subcategoryLandingPage}
                    element={<CategoryLandingPage />}
                  />
                  {/* Account pages */}
                  <Route path={urls.favorites} element={<MyFavorites />} />
                  <Route path={urls.my_account} element={<MyAccount />} />
                  <Route path={urls.my_orders} element={<MyOrders />} />
                  <Route path={urls.my_addresses} element={<MyAddresses />} />

                  {/* Other pages */}
                  <Route path={urls.about} element={<About />} />
                  <Route path={urls.contact} element={<Contact />} />
                  <Route path={urls.vacatures} element={<Vacatures />} />

                  <Route path={urls.login} element={<Login />} />
                  <Route path={urls.register} element={<Register />} />
                  <Route
                    path={urls.forgot_password}
                    element={<ForgotPassword />}
                  />
                  <Route path={urls.email_verified} element={<VerifyEmail />} />
                  <Route path={urls.privacy} element={<PrivacyPolicy />} />
                  <Route path={urls.cart} element={<Cart />} />
                  <Route path={urls.checkout} element={<Checkout />} />
                  <Route
                    path={urls.order_confirmed}
                    element={<OrderConfirmed />}
                  />
                  <Route path={urls.order_failed} element={<OrderFailed />} />
                  <Route
                    path={urls.order_processing + urls.document_id_param}
                    element={<OrderProcessing />}
                  />
                  <Route path={urls.not_found} element={<NotFound />} />
                  <Route
                    path={urls.delivery_returns}
                    element={<ShippingReturns />}
                  />
                  <Route path={urls.faq} element={<FAQ />} />
                  <Route
                    path={urls.unsubscribe_mails}
                    element={<UnsubscribeMails />}
                  />
                  <Route
                    path={urls.certificates}
                    element={<HalalCertificaten />}
                  />
                  {/* SEO pages */}
                  <Route path={urls.slager} element={<Slager />} />
                  <Route path={urls.slager_hoorn} element={<SlagerHoorn />} />
                  <Route
                    path={urls.slager_medemblik}
                    element={<SlagerMedemblik />}
                  />
                  <Route
                    path={urls.slager_enkhuizen}
                    element={<SlagerEnkhuizen />}
                  />
                  <Route path={urls.iq_grills} element={<IQGrills />} />
                  <Route
                    path={urls.vlees_groothandel}
                    element={<VleesGroothandel />}
                  />
                  <Route path={urls.horeca} element={<Horeca />} />
                  <Route path={urls.winkel} element={<Winkel />} />
                  <Route path={urls.recipes} element={<ReceptenOverview />} />
                  <Route path={urls.blog} element={<BlogOverview />} />

                  <Route path={urls.packaging} element={<Verpakking />} />
                  <Route path={urls.grasgevoerd} element={<Grasgevoerd />} />
                  <Route path={urls.kerst_vlees} element={<KerstVlees />} />
                  <Route path={urls.cadeaubon} element={<Cadeaubon />} />
                  <Route
                    path={urls.recipesDetail}
                    element={<RecipesDetail />}
                  />
                  <Route path={urls.blogDetail} element={<BlogDetail />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ScrollToTop>
            <CookieBanner />
            <LoyaltyProgramModule />
          </DeliveryAddressProvider>
        </Router>
      </ErrorBoundary>
    </>
  );
};

export default App;
